import { Camera, Loader2, MessageSquareHeart, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";

import { feedbackService, type TFeedbackContext } from "@/api/feedback";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from "@/components/ui";
import { FEEDBACK_ACCEPT, FEEDBACK_MAX_SIZE_MB, isFeedbackSupported } from "@/lib/uploadPolicy";
import { cn } from "@/utils";

const COMMENT_MAX = 4000;
const NPS_LABELS = { low: "Not likely", high: "Very likely" };
const ICON_HINT = "Give feedback — tell us what's working or where you're stuck";

type TFeedbackWidgetProps = {
  /** Where the widget was triggered from — sent to the backend as context. */
  context?: TFeedbackContext;
  /** Company the feedback relates to (optional, links analytics to the entity). */
  companyId?: string;
  /** Trigger button label. Defaults per context. */
  label?: string;
  /**
   * "pill" — labeled chip for distress states where an explicit callout earns
   * the pixels. "icon" — icon-only button for header/action slots; labelled
   * redundantly via aria-label + tooltip + title.
   */
  variant?: "pill" | "icon";
  className?: string;
};

const DEFAULT_LABELS: Record<TFeedbackContext, string> = {
  onboarding: "Give feedback",
  summary: "Give feedback",
  stuck: "Having trouble?",
  general: "Give feedback",
};

/**
 * Compact NPS + comment + screenshot feedback popover.
 * Idempotent: one client_request_id per form-open, so double-clicks/retries
 * can never create duplicate submissions.
 */
export function FeedbackWidget({
  context = "general",
  companyId,
  label,
  variant = "pill",
  className,
}: TFeedbackWidgetProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = useCallback(() => {
    setScore(null);
    setComment("");
    setScreenshot(null);
    setScreenshotPreview(null);
    setAttachmentError(null);
    requestIdRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next && !requestIdRef.current) requestIdRef.current = crypto.randomUUID();
      if (!next) resetForm();
    },
    [resetForm]
  );

  const pickScreenshot = useCallback((file: File | null) => {
    setAttachmentError(null);
    if (!file) return;
    if (!isFeedbackSupported(file.name)) {
      setAttachmentError(`Screenshots must be images (${FEEDBACK_ACCEPT})`);
      return;
    }
    if (file.size > FEEDBACK_MAX_SIZE_MB * 1024 * 1024) {
      setAttachmentError(`Screenshot must be under ${FEEDBACK_MAX_SIZE_MB}MB`);
      return;
    }
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  }, []);

  const clearScreenshot = useCallback(() => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [screenshotPreview]);

  const hasContent = score !== null || comment.trim().length > 0 || screenshot !== null;

  const submit = useCallback(async () => {
    if (!hasContent || submitting) return;
    setSubmitting(true);

    let attachmentFileId: string | undefined;
    if (screenshot) {
      const { data, error } = await feedbackService.uploadScreenshot(screenshot);
      if (error || !data?.data?.id) {
        // Screenshot failed — offer to continue without it rather than losing the draft.
        setAttachmentError("Screenshot upload failed — you can submit without it.");
        setScreenshot(null);
        setScreenshotPreview(null);
        setSubmitting(false);
        return;
      }
      attachmentFileId = data.data.id;
    }

    const { error } = await feedbackService.submitFeedback({
      client_request_id: requestIdRef.current ?? crypto.randomUUID(),
      nps_score: score ?? undefined,
      comment: comment.trim() || undefined,
      context,
      company_id: companyId,
      attachment_file_id: attachmentFileId,
      metadata: {
        page_path: window.location.pathname,
        locale: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });

    setSubmitting(false);
    if (error) {
      toast({
        variant: "destructive",
        title: "Couldn't send feedback",
        description: "Nothing was lost — try again in a moment.",
      });
      return; // draft preserved
    }

    toast({ title: "Thanks for the feedback!", description: "It goes straight to the team." });
    setOpen(false);
    resetForm();
  }, [hasContent, submitting, screenshot, score, comment, context, companyId, toast, resetForm]);

  const trigger =
    variant === "icon" ? (
      <button
        type="button"
        aria-label="Give feedback"
        title={ICON_HINT}
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
          className
        )}
      >
        <MessageSquareHeart className="h-5 w-5" />
      </button>
    ) : (
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-border bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm backdrop-blur transition-colors hover:border-primary/40 hover:text-foreground",
          className
        )}
      >
        <MessageSquareHeart className="h-3.5 w-3.5" />
        {label ?? DEFAULT_LABELS[context]}
      </button>
    );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
    >
      {variant === "icon" ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>{ICON_HINT}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      <PopoverContent
        className="w-[min(20rem,calc(100vw-2rem))]"
        align="end"
      >
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-foreground">How likely are you to recommend Fundy?</p>
            <div
              className="mt-2 flex items-center gap-1"
              role="radiogroup"
              aria-label="NPS score"
            >
              {Array.from({ length: 11 }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={score === i}
                  aria-label={`${i} out of 10`}
                  onClick={() => setScore(i)}
                  className={cn(
                    "h-7 w-6 rounded text-xs font-medium transition-colors",
                    score === i
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-primary/20"
                  )}
                >
                  {i}
                </button>
              ))}
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
              <span>{NPS_LABELS.low}</span>
              <span>{NPS_LABELS.high}</span>
            </div>
          </div>

          <div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
              placeholder="What happened? (optional)"
              className="min-h-20 resize-none text-sm"
              maxLength={COMMENT_MAX}
            />
            <div className="mt-0.5 flex items-center justify-between text-[10px] text-muted-foreground">
              <span>Avoid passwords or other sensitive info.</span>
              <span>
                {comment.length}/{COMMENT_MAX}
              </span>
            </div>
          </div>

          {screenshotPreview ? (
            <div className="relative inline-block">
              <img
                src={screenshotPreview}
                alt="Screenshot preview"
                className="max-h-24 rounded border border-border"
              />
              <button
                type="button"
                onClick={clearScreenshot}
                aria-label="Remove screenshot"
                className="absolute -right-2 -top-2 rounded-full bg-destructive p-0.5 text-destructive-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              <Camera className="h-3.5 w-3.5" />
              Add a screenshot
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={FEEDBACK_ACCEPT}
            className="hidden"
            aria-label="Attach screenshot"
            onChange={(e) => pickScreenshot(e.target.files?.[0] ?? null)}
          />
          {attachmentError && <p className="text-xs text-destructive">{attachmentError}</p>}

          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => void submit()}
              disabled={!hasContent || submitting}
            >
              {submitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Send feedback
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
